import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useProjectService, useWorkspaceService } from '@web/app/features/home/hooks';
import axios from 'axios';

import { useGetWorkspace } from '@codes/workspace';

import { useSelectedStore } from '../stores';

export enum PathError {
    InvalidWorkspace = 'invalidWorkspace',
    InvalidProject = 'invalidProject',
    NoAccessToWorkspace = 'noAccessToWorkspace',
    NoAccessToProject = 'noAccessToProject',
    WorkspaceNotFound = 'workspaceNotFound',
    ProjectNotFound = 'projectNotFound',
}

export const usePathGuard = () => {
    const [isChecking, setIsChecking] = useState(true);
    const [error, setError] = useState<PathError | null>(null);

    const { workspaceId, projectId } = useParams();

    const { mutateAsync: getWorkspace } = useGetWorkspace();

    const { setProject, setWorkspace } = useSelectedStore();
    const { myProject, myProjectsQueryResult, myProjects } = useProjectService();

    const { replaceWorkspace, workspaceFromServerQueryData, workspaceFromServer } = useWorkspaceService();

    useEffect(() => {
        const checkWorkspaceId = async () => {
            if (!workspaceId) {
                setIsChecking(false);
                console.log('No workspaceId in URL');
                return;
            }
            if (workspaceFromServerQueryData.isError) {
                console.log('Failed to fetch workspace from server.');
                setIsChecking(false);
                setProject(null);
                setWorkspace(null);

                return;
            }

            if (!workspaceFromServerQueryData.isSuccess) {
                console.log('Workspace data is still loading');
                return;
            }

            if (workspaceFromServer?.id !== workspaceId) {
                console.log('Workspace from server does not match the URL workspaceId, validating...');

                try {
                    const newWorkspace = await getWorkspace(workspaceId);
                    await replaceWorkspace(newWorkspace.id as string, newWorkspace.siteId as string);
                    checkProjectId(newWorkspace.id as string);
                } catch (e) {
                    if (axios.isAxiosError(e)) {
                        // Axios 에러인 경우
                        console.log('Axios error:', e.response?.status, e.response?.data);
                        if (e.response?.status === 404) {
                            setError(PathError.WorkspaceNotFound);
                        } else if (e.response?.status === 403) {
                            setError(PathError.NoAccessToWorkspace);
                        } else {
                            setError(PathError.InvalidWorkspace);
                        }
                    } else {
                        // 일반 에러인 경우
                        console.log('General error:', e);
                        setError(PathError.InvalidWorkspace);
                    }
                    setIsChecking(false);
                }
            } else {
                console.log('Workspace from server matches the URL workspaceId');
                checkProjectId(workspaceId);
            }
        };

        const checkProjectId = async (newWorkspaceId: string) => {
            if (!projectId) {
                setIsChecking(false);
                console.log('No projectId in URL');
                return;
            }
            if (myProject?.id === projectId) {
                setIsChecking(false);
                console.log('Already checked projectId');
                return;
            }

            if (!myProjectsQueryResult.isSuccess) {
                console.log('Project data is still loading');
                return;
            }

            const project = myProjects.find(project => project.id === projectId);

            if (!project) {
                setError(PathError.ProjectNotFound);
                setIsChecking(false);

                return;
            }

            if (project.workspaceId !== newWorkspaceId) {
                setError(PathError.NoAccessToProject);
            }
            setProject(project);
            setIsChecking(false);
        };

        checkWorkspaceId();
    }, [
        workspaceId,
        workspaceFromServerQueryData.isSuccess,
        workspaceFromServerQueryData.isError,
        workspaceFromServer?.id,
        projectId,
        myProject?.id,
        myProjectsQueryResult.isSuccess,
    ]);

    return { isChecking, error };
};
